import React, { useState } from 'react';
import { 
  KeyIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import Dialog from '../common/Dialog';
import { User } from '../../services/users';

export interface ResetPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  onResetPassword: (userId: string) => Promise<void>;
  loading?: boolean;
}

const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({
  isOpen,
  onClose,
  user,
  onResetPassword,
  loading = false
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleResetPassword = async () => {
    if (!user) return;

    try {
      setIsSubmitting(true);
      await onResetPassword(user.id);
      setShowConfirmation(true);
      
      // Fermer automatiquement après 3 secondes
      setTimeout(() => {
        setShowConfirmation(false);
        onClose();
      }, 3000);
    } catch (error: any) {
      console.error('❌ Erreur lors de la réinitialisation du mot de passe:', error);
      alert(`Erreur lors de la réinitialisation: ${error.message || 'Erreur inconnue'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setShowConfirmation(false);
    onClose();
  };

  if (!user) return null;

  if (showConfirmation) {
    return (
      <Dialog
        isOpen={isOpen}
        onClose={handleCancel}
        title="Mot de passe réinitialisé"
        size="md"
      >
        <div className="text-center space-y-6">
          <div className="flex justify-center">
            <div className="p-4 rounded-full bg-green-100">
              <CheckCircleIcon className="h-8 w-8 text-green-500" />
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Mot de passe réinitialisé avec succès !
            </h3>
            <p className="text-sm text-gray-500">
              Un nouveau mot de passe a été généré et envoyé par email à <strong>{user.email}</strong>
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <EnvelopeIcon className="h-5 w-5 text-green-400 mr-2" />
              <p className="text-sm text-green-700">
                <strong>Email envoyé :</strong> {user.email}
              </p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="h-5 w-5 text-blue-400 mr-2 mt-0.5" />
              <div className="text-left">
                <p className="text-sm font-medium text-blue-800 mb-1">Informations importantes :</p>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>• L'utilisateur devra se connecter avec le nouveau mot de passe</li>
                  <li>• Le mot de passe temporaire expire après 24h</li>
                  <li>• L'utilisateur sera invité à changer son mot de passe</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="pt-4">
            <button
              onClick={handleCancel}
              className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
            >
              Fermer
            </button>
          </div>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleCancel}
      title="Réinitialiser le mot de passe"
      size="md"
    >
      <div className="space-y-6">
        {/* Avertissement */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-yellow-800">
                Attention : Réinitialisation du mot de passe
              </h3>
              <p className="text-sm text-yellow-700 mt-1">
                Cette action va générer un nouveau mot de passe temporaire et l'envoyer par email à l'utilisateur.
              </p>
            </div>
          </div>
        </div>

        {/* Informations utilisateur */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
              <span className="text-orange-600 font-medium text-sm">
                {user.name?.split(' ').map((n: string) => n[0]).join('') || user.username?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900">{user.name || 'Nom non défini'}</h3>
              <p className="text-sm text-gray-500">@{user.username}</p>
              {user.email && (
                <div className="flex items-center mt-1">
                  <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-1" />
                  <span className="text-sm text-gray-600">{user.email}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Détails de l'opération */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Ce qui va se passer :</h4>
          <div className="space-y-3">
            <div className="flex items-start">
              <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3"></div>
              <div>
                <p className="text-sm font-medium text-gray-700">Génération d'un nouveau mot de passe</p>
                <p className="text-xs text-gray-500">Mot de passe temporaire sécurisé et aléatoire</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></div>
              <div>
                <p className="text-sm font-medium text-gray-700">Envoi par email</p>
                <p className="text-xs text-gray-500">Le nouveau mot de passe sera envoyé à {user.email}</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></div>
              <div>
                <p className="text-sm font-medium text-gray-700">Expiration automatique</p>
                <p className="text-xs text-gray-500">Le mot de passe temporaire expire après 24h</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3"></div>
              <div>
                <p className="text-sm font-medium text-gray-700">Changement obligatoire</p>
                <p className="text-xs text-gray-500">L'utilisateur devra changer son mot de passe à la prochaine connexion</p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={handleCancel}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
            disabled={loading || isSubmitting}
          >
            Annuler
          </button>
          <button
            onClick={handleResetPassword}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || isSubmitting}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Réinitialisation...
              </div>
            ) : (
              <div className="flex items-center justify-center">
                <KeyIcon className="h-4 w-4 mr-2" />
                Réinitialiser le mot de passe
              </div>
            )}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default ResetPasswordModal;
